import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { HiPlus } from "react-icons/hi";
import { SlPencil } from "react-icons/sl";

import { translation } from "../../translation";
import {
  useRemoveIdentificationTagFromVocabularyMutation,
  useSearchIdentificationContactsQuery,
  useUpdateIdentificationTagsMutation,
} from "../../redux/appSlices/collaborator/collaboratorApiSlice";
import { useSelector } from "react-redux";

const PeriodRangeInput = forwardRef(
  ({ value, onClick, placeholder, lang }, ref) => (
    <button
      type="button"
      onClick={onClick}
      ref={ref}
      className={`flex h-[50px] w-full items-center rounded-[12px] border border-inputBottom bg-transparent py-3 text-[14px] text-black transition-shadow focus:outline-none focus:ring-2 focus:ring-black/10 ${
        lang === "ar"
          ? "flex-row-reverse justify-between pr-4 pl-3"
          : "justify-start gap-3 pl-4 pr-3"
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-4 w-4 shrink-0 text-[#9b9b9b]"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <span
        className={`truncate ${lang === "ar" ? "w-full text-right" : "text-left"}`}
      >
        {value || placeholder}
      </span>
    </button>
  ),
);

PeriodRangeInput.displayName = "PeriodRangeInput";

const looksLikeEmail = (value) => {
  if (!value || typeof value !== "string") return false;
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

const openMailto = (email) => {
  const href = `mailto:${email}`;
  const a = document.createElement("a");
  a.href = href;
  a.setAttribute("rel", "noopener noreferrer");
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const openWhatsApp = (phone) => {
  const cleanPhone = phone.replace(/\D/g, "");
  const href = `https://wa.me/${cleanPhone}`;
  window.open(href, "_blank", "noopener,noreferrer");
};

const toLocalDateString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDisplayDate = (isoDateString) => {
  if (!isoDateString) return "-";
  const [datePart, timePart] = isoDateString.split("T");
  const [year, month, day] = datePart.split("-");

  if (timePart) {
    const [hours, minutes] = timePart.split(":");
    return `${day}/${month}/${year} à ${hours}:${minutes}`;
  }

  return `${day}/${month}/${year}`;
};

/** Ordered union: collaborator vocabulary first, then tags from contacts, then session-only custom tags. */
function mergeIdentificationTagVocabulary(
  collabTags,
  tagsFromContacts,
  custom,
) {
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const t = String(raw ?? "").trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };
  if (Array.isArray(collabTags)) collabTags.forEach(push);
  if (Array.isArray(tagsFromContacts)) tagsFromContacts.forEach(push);
  if (Array.isArray(custom)) custom.forEach(push);
  return out;
}

const MeetingsModal = ({ show, onClose, lang }) => {
  const collaborator = useSelector(
    (state) => state.collaborator.collaboratorInfo,
  );
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showTagSheet, setShowTagSheet] = useState(false);
  const [isTagSheetOpen, setIsTagSheetOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [customTags, setCustomTags] = useState([]);
  const [activeEncounterId, setActiveEncounterId] = useState(null);
  const [selectedTagsByEncounter, setSelectedTagsByEncounter] = useState({});
  const [contactsPage, setContactsPage] = useState(1);
  const [allContacts, setAllContacts] = useState([]);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [notesByEncounter, setNotesByEncounter] = useState({});
  const [currentNote, setCurrentNote] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const contactsLimit = 20;
  const [updateIdentificationTags] = useUpdateIdentificationTagsMutation();
  const [removeIdentificationTagFromVocabulary] =
    useRemoveIdentificationTagFromVocabularyMutation();

  const startDateParam = toLocalDateString(startDate);
  const endDateParam = toLocalDateString(endDate);
  const hasCompleteDateRange = Boolean(startDateParam && endDateParam);
  const dateFilterKey = hasCompleteDateRange
    ? `${startDateParam}:${endDateParam}`
    : "all";

  const {
    data: identificationContacts,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
    isError: isContactsError,
  } = useSearchIdentificationContactsQuery(
    {
      id: collaborator?._id,
      page: contactsPage,
      limit: contactsLimit,
      ...(hasCompleteDateRange
        ? { startDate: startDateParam, endDate: endDateParam }
        : {}),
    },
    { skip: !collaborator?._id },
  );

  const paginationTotal = identificationContacts?.pagination?.total;
  const meetingsTitleTotalSuffix =
    typeof paginationTotal === "number" && Number.isFinite(paginationTotal)
      ? ` (${paginationTotal})`
      : "";

  useEffect(() => {
    if (show) {
      setIsModalVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsModalVisible(true);
        });
      });
    } else {
      setIsModalVisible(false);
    }
  }, [show]);

  useEffect(() => {
    setContactsPage(1);
    setAllContacts([]);
  }, [collaborator?._id, dateFilterKey]);

  useEffect(() => {
    if (isFetchingContacts && contactsPage === 1) return;

    const contacts = identificationContacts?.contacts || [];
    const respPage = identificationContacts?.pagination?.page;

    if (contactsPage === 1) {
      if (respPage != null && respPage !== 1) return;
      setAllContacts(contacts);
      return;
    }

    if (!contacts.length) return;
    if (respPage != null && respPage !== contactsPage) return;

    setAllContacts((prev) => {
      const existingIds = new Set(prev.map((item) => item._id));
      const nextContacts = contacts.filter(
        (item) => !existingIds.has(item._id),
      );
      return [...prev, ...nextContacts];
    });
  }, [identificationContacts, contactsPage, isFetchingContacts]);

  useEffect(() => {
    const contacts = identificationContacts?.contacts || [];
    if (!contacts.length) return;

    setSelectedTagsByEncounter((prev) => {
      const next = { ...prev };
      contacts.forEach((contact) => {
        if (!next[contact._id] && Array.isArray(contact.tags)) {
          next[contact._id] = contact.tags;
        }
      });
      return next;
    });

    setNotesByEncounter((prev) => {
      const next = { ...prev };
      contacts.forEach((contact) => {
        if (next[contact._id] !== undefined) return;
        next[contact._id] = contact.notes ?? "";
      });
      return next;
    });
  }, [identificationContacts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId) {
        const target = event.target;
        const isClickInsideMenu = target.closest(".action-menu-popup");
        const isClickOnButton = target.closest(".action-menu-button");

        if (!isClickInsideMenu && !isClickOnButton) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const encounters = useMemo(() => {
    return allContacts.map((contact) => {
      const fullName =
        `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim();
      const rawEmail = (contact?.email || "").trim();
      const hasEmail = looksLikeEmail(rawEmail);
      const companyTrim = (contact?.company || "").trim();
      const positionTrim = (contact?.function || "").trim();

      const phoneRaw =
        contact?.phone || contact?.phone1 || contact?.phone2 || "";
      const phoneDisplay = String(phoneRaw).trim();

      const tagsResolved =
        selectedTagsByEncounter[contact?._id] ??
        (Array.isArray(contact?.tags) ? contact.tags : []);

      const notesResolved =
        notesByEncounter[contact?._id] ??
        String(contact?.notes ?? "");

      return {
        id: contact?._id,
        date: formatDisplayDate(contact?.createdAt),
        name: fullName,
        company: companyTrim,
        position: positionTrim,
        emailAddress: hasEmail ? rawEmail : "",
        phoneDisplay,
        tags: tagsResolved,
        notesText: notesResolved,
        contactFileUrl: contact?.contactFileUrl || "",
      };
    });
  }, [allContacts, selectedTagsByEncounter, notesByEncounter]);

  const identifIdForRowId = (rowContactId) => {
    const row = allContacts.find((c) => c._id === rowContactId);
    if (!row) return rowContactId;
    return row.identification_id || row._id;
  };

  const handleContactsScroll = (event) => {
    if (isFetchingContacts || isLoadingContacts) return;
    if (!identificationContacts?.pagination?.hasNextPage) return;
    const respPage = identificationContacts?.pagination?.page;
    if (respPage != null && contactsPage !== respPage) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
    if (isNearBottom) {
      setContactsPage((prev) => prev + 1);
    }
  };

  const filteredEncounters = encounters.filter((item) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const tagMatch =
      Array.isArray(item.tags) &&
      item.tags.some((tag) =>
        String(tag).toLowerCase().includes(q),
      );
    const notesMatch =
      item.notesText &&
      String(item.notesText).toLowerCase().includes(q);
    return (
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.company && item.company.toLowerCase().includes(q)) ||
      (item.position && item.position.toLowerCase().includes(q)) ||
      (item.emailAddress && item.emailAddress.toLowerCase().includes(q)) ||
      (item.phoneDisplay && item.phoneDisplay.toLowerCase().includes(q)) ||
      tagMatch ||
      notesMatch
    );
  });

  const tagsFromLoadedContacts = useMemo(
    () => allContacts.flatMap((c) => (Array.isArray(c.tags) ? c.tags : [])),
    [allContacts],
  );

  const allAvailableTags = useMemo(
    () =>
      mergeIdentificationTagVocabulary(
        collaborator?.identification_tags,
        tagsFromLoadedContacts,
        customTags,
      ),
    [collaborator?.identification_tags, tagsFromLoadedContacts, customTags],
  );
  const normalizedTagSearch = tagSearch.trim().toLowerCase();
  const selectedTags = selectedTagsByEncounter[activeEncounterId] || [];
  const filteredTags = allAvailableTags.filter(
    (tag) =>
      tag.toLowerCase().includes(normalizedTagSearch) &&
      !selectedTags.includes(tag),
  );
  const tagAlreadyExists = allAvailableTags.some(
    (tag) => tag.toLowerCase() === normalizedTagSearch,
  );

  const selectedTagsVisibleInResults = normalizedTagSearch
    ? selectedTags.filter((tag) =>
        tag.toLowerCase().includes(normalizedTagSearch),
      )
    : selectedTags;

  const showTagsVocabularyEmpty =
    allAvailableTags.length === 0 &&
    !(normalizedTagSearch && !tagAlreadyExists);

  const addTagToActiveEncounter = (tag) => {
    if (!activeEncounterId) return;
    const currentTags = selectedTagsByEncounter[activeEncounterId] || [];
    if (currentTags.includes(tag)) return;
    const nextTags = [...currentTags, tag];

    setSelectedTagsByEncounter((prev) => {
      return {
        ...prev,
        [activeEncounterId]: nextTags,
      };
    });

    setIsSavingTags(true);
    updateIdentificationTags({
      identifId: identifIdForRowId(activeEncounterId),
      tags: nextTags,
      collaboratorId: collaborator._id,
    })
      .unwrap()
      .catch((error) => {
        console.error(error);
        setSelectedTagsByEncounter((prev) => ({
          ...prev,
          [activeEncounterId]: currentTags,
        }));
      })
      .finally(() => setIsSavingTags(false));
  };

  const removeTagFromActiveEncounter = (tag) => {
    if (!activeEncounterId) return;
    const currentTags = selectedTagsByEncounter[activeEncounterId] || [];
    const nextTags = currentTags.filter((t) => t !== tag);

    setSelectedTagsByEncounter((prev) => {
      return {
        ...prev,
        [activeEncounterId]: nextTags,
      };
    });

    setIsSavingTags(true);
    updateIdentificationTags({
      identifId: identifIdForRowId(activeEncounterId),
      tags: nextTags,
      collaboratorId: collaborator._id,
    })
      .unwrap()
      .catch((error) => {
        console.error(error);
        setSelectedTagsByEncounter((prev) => ({
          ...prev,
          [activeEncounterId]: currentTags,
        }));
      })
      .finally(() => setIsSavingTags(false));
  };

  const deleteTag = (tag) => {
    const trimmed = String(tag ?? "").trim();
    if (!trimmed || !collaborator?._id) return;

    setCustomTags((prev) => prev.filter((t) => t !== trimmed));
    setSelectedTagsByEncounter((prev) => {
      const next = {};
      Object.keys(prev).forEach((encounterId) => {
        next[encounterId] = (prev[encounterId] || []).filter(
          (t) => t !== trimmed,
        );
      });
      return next;
    });

    setIsSavingTags(true);
    removeIdentificationTagFromVocabulary({
      collaboratorId: collaborator._id,
      tag: trimmed,
    })
      .unwrap()
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setIsSavingTags(false));
  };

  const openTagSheet = (encounterId) => {
    setActiveEncounterId(encounterId);
    setShowTagSheet(true);
    setTagSearch("");
    requestAnimationFrame(() => setIsTagSheetOpen(true));
  };

  const closeTagSheet = () => {
    setIsTagSheetOpen(false);
    setTagSearch("");
    setTimeout(() => setShowTagSheet(false), 300);
  };

  const openNoteModal = (encounterId) => {
    setActiveEncounterId(encounterId);
    setCurrentNote(notesByEncounter[encounterId] || "");
    setShowNoteModal(true);
    requestAnimationFrame(() => setIsNoteModalOpen(true));
  };

  const closeNoteModal = () => {
    setIsNoteModalOpen(false);
    setTimeout(() => {
      setShowNoteModal(false);
      setCurrentNote("");
    }, 300);
  };

  const saveNote = () => {
    if (!activeEncounterId) return;
    const noteText = currentNote.trim();
    const identifId = identifIdForRowId(activeEncounterId);
    
    console.log("Saving note:", {
      identifId,
      notes: noteText,
      collaboratorId: collaborator._id,
    });
    
    setNotesByEncounter((prev) => ({
      ...prev,
      [activeEncounterId]: noteText,
    }));

    setIsSavingNote(true);
    updateIdentificationTags({
      identifId,
      notes: noteText,
      collaboratorId: collaborator._id,
    })
      .unwrap()
      .then((result) => {
        console.log("Note saved successfully:", result);
        closeNoteModal();
      })
      .catch((error) => {
        console.error("Error saving note:", error);
        setNotesByEncounter((prev) => {
          const next = { ...prev };
          delete next[activeEncounterId];
          return next;
        });
      })
      .finally(() => setIsSavingNote(false));
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col overflow-hidden bg-popcard">
      <style>
        {`
          .react-datepicker__day--selected,
          .react-datepicker__day--in-range,
          .react-datepicker__day--in-selecting-range,
          .react-datepicker__day--range-start,
          .react-datepicker__day--range-end {
            background-color: #000000 !important;
            color: white !important;
          }
          .react-datepicker__day--selected:hover,
          .react-datepicker__day--in-range:hover,
          .react-datepicker__day--range-start:hover,
          .react-datepicker__day--range-end:hover {
            background-color: #1a1a1a !important;
          }
          .react-datepicker__day--keyboard-selected {
            background-color: #000000 !important;
            color: white !important;
          }
          .react-datepicker__day--keyboard-selected:hover {
            background-color: #1a1a1a !important;
          }
        `}
      </style>
      <div
        className={`max-w-[580px] m-auto flex h-full min-h-0 w-full flex-col transition-all duration-300 ${
          isModalVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-4 opacity-0"
        }`}
      >
        <div
          className={`flex justify-end mt-9 ${lang === "ar" ? "ml-5" : "mr-5"}`}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={translation?.meetingsModal?.close?.[lang]}
            className={`cursor-pointer flex justify-end  ${lang === "ar" ? "ml-5" : "mr-5"}} `}
          >
            <img src="../../assets/img/icons/close.svg" alt="close popup" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-[20px] pb-[24px]">
          <div className="mb-6">
            <div
              className={`text-2xl ${lang === "ar" ? "font-lalezarRegular" : "font-SteradianMedium"}`}
            >
              {`${translation?.meetingsModal?.title?.[lang] ?? ""}${meetingsTitleTotalSuffix}`}
            </div>
          </div>

          <div className="mb-4 relative">
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                const [start, end] = dates;
                setStartDate(start);
                setEndDate(end);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              dateFormat="dd/MM/yyyy"
              wrapperClassName="w-full"
              // popperPlacement="bottom-start"
              // calendarClassName="!border !border-black !rounded-[16px] !shadow-xl"
              customInput={
                <PeriodRangeInput
                  lang={lang}
                  placeholder={translation?.meetingsModal?.choosePeriod?.[lang]}
                />
              }
              // className="text-black bg-transparent"
              title={translation?.meetingsModal?.choosePeriod?.[lang]}
              placeholderText={translation?.meetingsModal?.choosePeriod?.[lang]}
            />
            {(startDate || endDate) && (
              <button
                type="button"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
                className={`absolute top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors duration-150 ${
                  lang === "ar" ? "left-4" : "right-4"
                }`}
                aria-label="Clear dates"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-3.5 w-3.5 text-black"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="relative mb-5">
            <span
              className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[#9b9b9b] ${
                lang === "ar" ? "right-4" : "left-4"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="16.65" y1="16.65" x2="21" y2="21" />
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                translation?.meetingsModal?.searchPlaceholder?.[lang]
              }
              className={`h-[50px] w-full rounded-[12px] bg-transparent py-3 text-[16px] text-black outline-none border border-inputBottom transition-shadow focus:ring-2 placeholder:text-black focus:ring-black/10 ${
                lang === "ar" ? "pr-10 pl-12" : "pl-10 pr-12"
              }`}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className={`absolute top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors duration-150 ${
                  lang === "ar" ? "left-4" : "right-4"
                }`}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-3.5 w-3.5 text-black"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-1 text-[#6b6b6b] no-scrollbar"
            onScroll={handleContactsScroll}
          >
            {isLoadingContacts && contactsPage === 1 ? (
              <p className="py-4 text-center text-[14px] text-[#6a6a6a]">
                Loading...
              </p>
            ) : null}

            {isContactsError ? (
              <p className="py-4 text-center text-[14px] text-red-600">
                Failed to load contacts.
              </p>
            ) : null}

            {!isLoadingContacts &&
            !isContactsError &&
            filteredEncounters.length === 0 ? (
              <p className="py-4 text-center text-[14px] text-[#6a6a6a]">
                Aucun contact pour le moment.
              </p>
            ) : null}

            {filteredEncounters.map((person) => (
              <div
                key={person.id}
                className="border-b border-black/10 py-4 relative"
              >
                <div
                  className={`mb-2 flex gap-2 items-center absolute top-2 ${
                    lang === "ar"
                      ? "justify-start left-2"
                      : "justify-end right-2"
                  }`}
                >
                  {notesByEncounter[person.id] && (
                    <button
                      type="button"
                      onClick={() => openNoteModal(person.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black bg-transparent text-black text-[13px] hover:bg-black/5 transition-colors duration-150"
                    >
                      <span>
                        {translation?.meetingsModal?.actions?.notes?.[lang]}
                      </span>
                      <SlPencil size={14} className="mb-1" />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={translation?.mainButton?.addContact?.[lang]}
                    className="action-menu-button flex size-6 items-center justify-center rounded-full bg-black text-white text-[22px] leading-none shadow-md hover:shadow-lg transition-shadow duration-200"
                    onClick={() =>
                      setOpenMenuId((prev) =>
                        prev === person.id ? null : person.id,
                      )
                    }
                  >
                    <HiPlus size={16} />
                  </button>
                  {openMenuId === person.id && (
                    <div
                      className={`action-menu-popup absolute top-10 z-20 min-w-[220px] overflow-hidden rounded-[12px] border border-black/10 bg-white shadow-xl ${
                        lang === "ar" ? "left-0" : "right-0"
                      }`}
                    >
                      {person.emailAddress && (
                        <button
                          type="button"
                          className="block w-full px-4 py-3 text-left text-[14px] text-black hover:bg-[#f8f8f8] transition-colors duration-150"
                          onClick={() => {
                            openMailto(person.emailAddress);
                            setOpenMenuId(null);
                          }}
                        >
                          {
                            translation?.meetingsModal?.actions?.sendEmail?.[
                              lang
                            ]
                          }
                        </button>
                      )}
                      <button
                        type="button"
                        className={`block w-full px-4 py-3 text-left text-[14px] text-black hover:bg-[#f8f8f8] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                          person.emailAddress ? "border-t border-black/5" : ""
                        }`}
                        disabled={!person.contactFileUrl}
                        onClick={() => {
                          if (person.contactFileUrl) {
                            window.open(
                              person.contactFileUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }
                          setOpenMenuId(null);
                        }}
                      >
                        {
                          translation?.meetingsModal?.actions
                            ?.addToAddressBook?.[lang]
                        }
                      </button>
                      <button
                        type="button"
                        className="block w-full border-t border-black/5 px-4 py-3 text-left text-[14px] text-black hover:bg-[#f8f8f8] transition-colors duration-150"
                        onClick={() => {
                          openTagSheet(person.id);
                          setOpenMenuId(null);
                        }}
                      >
                        {translation?.meetingsModal?.actions?.addTag?.[lang]}
                      </button>
                      <button
                        type="button"
                        className="block w-full border-t border-black/5 px-4 py-3 text-left text-[14px] text-black hover:bg-[#f8f8f8] transition-colors duration-150"
                        onClick={() => {
                          openNoteModal(person.id);
                          setOpenMenuId(null);
                        }}
                      >
                        {translation?.meetingsModal?.actions?.writeNote?.[lang]}
                      </button>
                    </div>
                  )}
                </div>

                <p className="mb-2 text-[13px] text-[#999]">{person.date}</p>
                {person.company && (
                  <p className="mb-2 text-[14px] text-[#666]">
                    {person.company}
                  </p>
                )}
                {person.name && (
                  <h3 className="text-[18px] font-semibold text-black mb-2">
                    {person.name}
                  </h3>
                )}
                {person.position && (
                  <p className="mb-3 text-[15px] text-[#666]">
                    {person.position}
                  </p>
                )}

                {(person.emailAddress || person.phoneDisplay) && (
                  <div className="mb-4 space-y-1 text-[14px] text-black">
                    {person.emailAddress && (
                      <button
                        type="button"
                        onClick={() => openMailto(person.emailAddress)}
                        className="underline decoration-1 underline-offset-2 hover:text-black/70 transition-colors duration-150 text-left"
                      >
                        {person.emailAddress}
                      </button>
                    )}
                    {person.phoneDisplay && (
                      <button
                        type="button"
                        onClick={() => openWhatsApp(person.phoneDisplay)}
                        className="underline decoration-1 underline-offset-2 hover:text-black/70 transition-colors duration-150 text-left block"
                      >
                        {person.phoneDisplay}
                      </button>
                    )}
                  </div>
                )}

                {person.tags.length > 0 && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-2">
                      {person.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-black px-4 py-2 text-[13px] font-medium text-white"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {isFetchingContacts && contactsPage > 1 ? (
              <p className="py-3 text-center text-[13px] text-[#6a6a6a]">
                Loading more...
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {showTagSheet && (
        <>
          <button
            type="button"
            aria-label="Close tag sheet"
            className={`fixed inset-0 z-[10001] bg-black/20 transition-opacity duration-300 ${
              isTagSheetOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={closeTagSheet}
          />
          <div
            className={`fixed bottom-0 left-0 right-0 z-[10002] flex flex-col rounded-t-[20px] border-t border-black/10 bg-[#FFFAF4] shadow-[0_-4px_20px_rgba(0,0,0,0.12)] transition-all duration-300 ${
              isTagSheetOpen
                ? "translate-y-0 opacity-100"
                : "translate-y-full opacity-0"
            }`}
            style={{ height: "55vh", maxHeight: "600px" }}
          >
            <div className="shrink-0 px-5 pt-4 pb-3">
              <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-[#d8d8d8]" />
              {isSavingTags ? (
                <p className="mb-2 text-center text-[12px] text-[#6a6a6a]">
                  Saving tags...
                </p>
              ) : null}
              <div className="relative mt-4">
                <div className="w-full rounded-[12px] border border-inputBottom bg-transparent px-3 py-2.5 shadow-sm">
                  <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap no-scrollbar">
                    {selectedTags.map((tag) => (
                      <span
                        key={`selected-chip-${tag}`}
                        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-black px-3 py-1.5 text-[12px] font-medium text-white shadow-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                          onClick={() => removeTagFromActiveEncounter(tag)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            className="h-2.5 w-2.5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagSearch}
                      onChange={(e) => setTagSearch(e.target.value)}
                      placeholder={
                        selectedTags.length
                          ? ""
                          : translation?.meetingsModal?.actions
                              ?.searchTagPlaceholder?.[lang]
                      }
                      className={`min-h-[28px] min-w-[140px] flex-1 self-center bg-transparent text-[16px] text-black outline-none placeholder:text-[#9b9b9b] ${
                        lang === "ar" ? "text-right pr-2" : "pl-2"
                      }`}
                    />
                  </div>
                </div>
              </div>

              {showTagsVocabularyEmpty ? (
                <p
                  className={`mt-6 text-[14px] leading-snug text-[#9b9b9b] ${
                    lang === "ar" ? "text-right" : "text-left"
                  }`}
                >
                  {translation?.meetingsModal?.actions?.tagsEmptyHint?.[lang]}
                </p>
              ) : null}
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-6">
              <div className="space-y-2">
                <div className="space-y-2">
                  {filteredTags.map((tag) => (
                    <div
                      key={`available-row-${tag}`}
                      className="flex w-full items-center justify-between rounded-[12px] border border-inputBottom bg-white px-4 py-1.5 text-[14px] text-black shadow-sm hover:shadow-md transition-shadow"
                    >
                      <button
                        type="button"
                        className="flex-1 text-left font-medium"
                        onClick={() => addTagToActiveEncounter(tag)}
                      >
                        {tag}
                      </button>
                      <button
                        type="button"
                        className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-black/5 transition-colors"
                        onClick={() => deleteTag(tag)}
                        aria-label={
                          translation?.meetingsModal?.actions?.deleteTag?.[lang]
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4 text-[#666]"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                          <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  ))}

                  {normalizedTagSearch &&
                    filteredTags.length === 0 &&
                    !tagAlreadyExists && (
                      <div className="flex items-center justify-between rounded-[12px] border-[1px] border-inputBottom  px-4 py-1.5 shadow-sm">
                        <span className="text-[14px] font-medium text-black">
                          {tagSearch.trim()}
                        </span>
                        <button
                          type="button"
                          className="ml-3 rounded-full bg-black px-4 py-1 text-[13px] font-medium text-white shadow-sm hover:shadow-md transition-shadow"
                          onClick={() => {
                            const newTag = tagSearch.trim();
                            setCustomTags((prev) => [...prev, newTag]);
                            addTagToActiveEncounter(newTag);
                            setTagSearch("");
                          }}
                        >
                          {
                            translation?.meetingsModal?.actions?.createTag?.[
                              lang
                            ]
                          }
                        </button>
                      </div>
                    )}

                  {selectedTagsVisibleInResults.map((tag) => (
                    <div
                      key={`selected-row-${tag}`}
                      className="flex w-full items-center justify-between rounded-[12px] border border-black/30 bg-black/5 px-4 py-1.5  text-[14px] text-black"
                    >
                      <span className="font-medium">{tag}</span>
                      <button
                        type="button"
                        className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-black/10 transition-colors"
                        onClick={() => deleteTag(tag)}
                        aria-label={
                          translation?.meetingsModal?.actions?.deleteTag?.[lang]
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4 text-[#666]"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                          <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showNoteModal && (
        <>
          <button
            type="button"
            aria-label="Close note modal"
            className={`fixed inset-0 z-[10003] bg-black/30 transition-opacity duration-300 ${
              isNoteModalOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={closeNoteModal}
          />
          <div
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[10004] w-[90%] max-w-[500px] rounded-[20px] bg-popcard shadow-[0_8px_32px_rgba(0,0,0,0.12)] transition-all duration-300 ${
              isNoteModalOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
            }`}
          >
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3
                  className={`text-[20px] font-semibold text-black ${
                    lang === "ar" ? "text-right" : "text-left"
                  }`}
                >
                  {notesByEncounter[activeEncounterId]
                    ? translation?.meetingsModal?.actions?.noteModalTitleView?.[
                        lang
                      ]
                    : translation?.meetingsModal?.actions?.noteModalTitle?.[
                        lang
                      ]}
                </h3>
                <button
                  type="button"
                  onClick={closeNoteModal}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5 transition-colors"
                  aria-label={translation?.meetingsModal?.close?.[lang]}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-5 w-5 text-black"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <textarea
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                placeholder={
                  translation?.meetingsModal?.actions?.notePlaceholder?.[lang]
                }
                className={`w-full min-h-[200px] p-4 rounded-[12px] border border-inputBottom bg-transparent text-[16px] text-black resize-none outline-none focus:ring-2 focus:ring-black/10 placeholder:text-[#9b9b9b] transition-shadow ${
                  lang === "ar" ? "text-right" : "text-left"
                }`}
                dir={lang === "ar" ? "rtl" : "ltr"}
              />

              <div
                className={`mt-4 flex gap-3 ${
                  lang === "ar"
                    ? "justify-start flex-row-reverse"
                    : "justify-end"
                }`}
              >
                <button
                  type="button"
                  onClick={closeNoteModal}
                  disabled={isSavingNote}
                  className="px-5 py-2.5 rounded-[12px] border border-black/20 bg-transparent text-black text-[14px] font-medium hover:bg-black/5 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {translation?.meetingsModal?.actions?.cancel?.[lang]}
                </button>
                <button
                  type="button"
                  onClick={saveNote}
                  disabled={isSavingNote}
                  className="px-5 py-2.5 rounded-[12px] bg-black text-white text-[14px] font-medium hover:bg-black/90 transition-colors duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingNote
                    ? "..."
                    : translation?.meetingsModal?.actions?.save?.[lang]}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MeetingsModal;
